import { UIMessage } from "ai";
import { AssistantLoadingIndicator, MessageView } from "../../../components/shared/chat/message";
import React, { ReactElement } from "react";
import { TekkrMarkdown } from "../../../components/shared/markdown/tekkr-markdown";
import { CheckIcon, WrenchIcon, XCircleIcon } from "lucide-react";
import { ChatToolName, chatToolRegistry } from "tekkr-common/dist/model/chats/tools";
import Spinner from "../../../components/ui/spinner";
import { isInvocationWithType, TypedToolInvocation, validatedToolInvocation } from "./utils";
import { SuggestMeetingToolInvocation } from "./tools/schedule-meeting";
import { SuggestPlanChangesToolInvocation } from "./tools/suggest-plan-changes";
import { ProposeDocumentUpdateToolInvocation } from "./tools/propose-document-update";
import { ApplyPlanChangesToolInvocation } from "./tools/apply-plan-changes";
import { GoogleDriveContextToolInvocation } from "./tools/google-drive-context";
import { ProjectChatUiContext } from "tekkr-common/dist/model/project/chat";
import { StatusUpdateMessageActions } from "./status-update-message-actions";

export function MessagesThread(props: { messages: UIMessage[]; isPending: boolean; uiContext?: ProjectChatUiContext }) {
    return (
        <div className={"mb-14 flex flex-col gap-4 px-4 pt-8"}>
            {props.messages.map((m, i) => (
                <MessagePartsView key={i} message={m} isPending={props.isPending} uiContext={props.uiContext} />
            ))}
            {props.isPending && <AssistantLoadingIndicator />}
        </div>
    );
}

function nextPartIsSameToolInvocation(parts: UIMessage["parts"], i: number) {
    if (parts.length <= i + 1) {
        return false;
    }
    const currentPart = parts[i];
    const nextPart = parts[i + 1];
    return (
        currentPart.type === "tool-invocation" &&
        nextPart.type === "tool-invocation" &&
        currentPart.toolInvocation.toolName === nextPart.toolInvocation.toolName
    );
}

function FailedToolInvocation({ invocation }: { invocation: TypedToolInvocation<ChatToolName> }) {
    const toolName = chatToolRegistry[invocation.toolName].name;
    return (
        <div className="flex flex-row items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-2 px-4 text-sm font-medium text-destructive">
            <XCircleIcon className="h-5 w-5 shrink-0" />
            <span>"{toolName}" failed. Please retry.</span>
        </div>
    );
}

function MessagePartsView({
    message,
    isPending,
    uiContext,
}: {
    message: UIMessage;
    isPending: boolean;
    uiContext?: ProjectChatUiContext;
}) {
    const partViews: ReactElement[] = [];
    let genericToolCallCounter = 0;
    message.parts.forEach((part, i) => {
        if (part.type === "text") {
            partViews.push(<TekkrMarkdown key={part.text} className={""} markdown={part.text} />);
        } else if (part.type === "tool-invocation") {
            const invocation = validatedToolInvocation(part.toolInvocation);
            // Check for failed tool invocation first (stream done but no result)
            if (invocation.state === "call" && !isPending) {
                partViews.push(<FailedToolInvocation key={invocation.toolCallId} invocation={invocation} />);
            } else if (isInvocationWithType("suggestMeeting", invocation)) {
                partViews.push(<SuggestMeetingToolInvocation key={invocation.toolCallId} invocation={invocation} />);
            } else if (isInvocationWithType("suggestPlanChanges", invocation)) {
                partViews.push(<SuggestPlanChangesToolInvocation invocation={invocation} />);
            } else if (isInvocationWithType("proposeDocumentUpdate", invocation)) {
                partViews.push(
                    <ProposeDocumentUpdateToolInvocation key={invocation.toolCallId} invocation={invocation} />
                );
            } else if (isInvocationWithType("applyPlanChanges", invocation)) {
                partViews.push(<ApplyPlanChangesToolInvocation key={invocation.toolCallId} invocation={invocation} />);
            } else if (isInvocationWithType("getGoogleDriveContext", invocation)) {
                partViews.push(
                    <GoogleDriveContextToolInvocation key={invocation.toolCallId} invocation={invocation} />
                );
            } else {
                // generic tool invocation
                if (nextPartIsSameToolInvocation(message.parts, i)) {
                    genericToolCallCounter++;
                } else {
                    partViews.push(
                        <GenericToolInvocation
                            key={invocation.toolCallId}
                            invocation={invocation}
                            count={genericToolCallCounter + 1}
                        />
                    );
                    genericToolCallCounter = 0;
                }
            }
        }
    });

    const showStatusUpdateActions = message.role === "assistant" && uiContext?.type === "status-update" && !isPending;

    return (
        <MessageView message={message}>
            <div className={"flex flex-col items-start gap-3"}>
                {partViews}
                {showStatusUpdateActions && <StatusUpdateMessageActions message={message} />}
            </div>
        </MessageView>
    );
}

function GenericToolInvocation(props: { invocation: TypedToolInvocation<ChatToolName>; count?: number }) {
    return (
        <div
            className={
                "flex flex-row items-center gap-3 rounded-md border bg-accent p-2 text-sm font-medium text-muted-foreground"
            }
        >
            <WrenchIcon className={"h-5 w-5"} />{" "}
            {props.count && props.count > 1 && (
                <div className={"rounded-full bg-background px-2 py-0.5 text-xs font-semibold"}>{props.count}x</div>
            )}
            <div>{chatToolRegistry[props.invocation.toolName as ChatToolName].name}</div>
            {props.invocation.state !== "result" ? (
                <Spinner className={"h-4 w-4"} />
            ) : (
                <CheckIcon className={"h-4 w-4"} />
            )}
        </div>
    );
}
